/* eslint-disable react-hooks/exhaustive-deps */
import * as React from "react";
import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Eye, EyeOff } from "lucide-react";
import ErrorAlert from "../error-alert";

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  containerClassName?: string;
  charactersOnly?: boolean;
  hasError?: boolean;
  errorMessage?: string;
}

const FloatInput = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      placeholder,
      id,
      charactersOnly,
      containerClassName,
      hasError,
      errorMessage,
      ...props
    },
    ref
  ) => {
    const defaultID = React.useId();
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const [inputType, setInputType] = React.useState(type || "text");

    React.useEffect(() => {
      const input = document.getElementById(id ?? defaultID);
      if (input && type === "number") {
        input.addEventListener("wheel", (e) => e.preventDefault());
      }

      return () => {
        input?.removeEventListener("wheel", (e) => e.preventDefault());
      };
    }, [id, defaultID]);

    const restrictInput = (e: React.FormEvent<HTMLInputElement>) => {
      const regex = /^[\p{L}]*$/u; // Unicode letter characters only
      if (!regex.test(e.currentTarget.value) && charactersOnly) {
        e.currentTarget.value = e.currentTarget.value.replace(/[^\p{L}]/gu, ""); // Remove non-letter characters
      }
    };

    return (
      <div className={cn("relative", containerClassName)}>
        <input
          id={id ?? defaultID}
          type={inputType}
          className={cn(
            "flex h-14 w-full rounded-2xl border border-slate-200 bg-white px-3 pt-4 pb-0 text-base ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[#ECECEC] dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300",
            "[&+label]:focus:!text-[12px] [&+label]:focus:!text-muted [&+label]:focus:font-light [&+label]:focus:translate-x-0 [&+label]:focus:-translate-y-12",
            {
              "[&+label]:!text-[12px] [&+label]:font-light [&+label]:!text-muted [&+label]:translate-x-0 [&+label]:-translate-y-12":
                inputRef.current?.value || props.value || props.value === 0,
              "border-error !ring-error": hasError,
            },

            className
          )}
          ref={(node) => {
            inputRef.current = node;
            if (typeof ref === "function") {
              ref(node);
            } else if (ref) {
              (ref as React.RefObject<HTMLInputElement | null>).current = node;
            }
          }}
          {...props}
          aria-autocomplete="none"
          autoComplete="none"
          onInput={restrictInput}
        />
        <label
          htmlFor={id ?? defaultID}
          className={cn(
            "transition-all pointer-events-none select-none text-sm text-gray-dark absolute ltr:left-3 rtl:right-3 -translate-y-10"
          )}
        >
          {placeholder}{" "}
          {props.required && <span className="text-red-500">*</span>}
        </label>
        {type === "password" ? (
          <Button
            type="button"
            onClick={() =>
              setInputType((prev) => (prev === "text" ? "password" : "text"))
            }
            className="absolute top-1/2 -translate-y-1/2 ltr:right-3 rtl:left-3"
            variant={"ghost"}
            size={"icon"}
          >
            {inputType === "text" ? <EyeOff /> : <Eye />}
          </Button>
        ) : null}
        {hasError && <ErrorAlert title={errorMessage} />}
      </div>
    );
  }
);
FloatInput.displayName = "FloatInput";

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, id, hasError, errorMessage, ...props }, ref) => {
    const defaultID = React.useId();

    return (
      <input
        id={id ?? defaultID}
        type={type}
        className={cn(
          "flex h-14 w-full rounded-2xl border placeholder:text-muted border-slate-200 bg-white px-3 py-2  ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed dark:border-slate-800 dark:bg-slate-950 dark:ring-offset-slate-950 dark:placeholder:text-slate-400 dark:focus-visible:ring-slate-300",
          className
        )}
        ref={ref}
        {...props}
        aria-autocomplete="none"
        autoComplete="none"
      />
    );
  }
);
Input.displayName = "Input";

export { FloatInput, Input };
